import gsap from 'gsap';
import { observeScrollAnimationTriggerElement } from '~/lib/animations';
import { OKENDO_STORE_URL } from '~/lib/constants';
import { generateStarRating } from '~/lib/helpers';
import { calculateDateDifference } from '~/utils/calendar-utils';

class ReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperLoader = this.querySelector('swiper-loader');
    this.swiperContainer = this.querySelector('swiper-container');
    this.aggregatedRatingContainer = this.querySelector('.reviews-carousel__aggregated-rating');
    this.isAnimationEnabled = this.getAttribute('data-animation-enabled') === 'true';
    this.productIDs = this.getAttribute('data-product-ids') ? this.getAttribute('data-product-ids').split(',') : [];
  }

  connectedCallback() {
    this.renderReviews();
    this.renderAggregatedRating();
  }

  async renderReviews() {
    let url = OKENDO_STORE_URL + '/reviews';

    if (this.productIDs.length > 0) {
      url += '?limit=250';
    }

    const reviews = await this.fetchReviews(url);
    reviews.forEach((review) => {
      if (!review?.productImageUrl || review.productHandle?.includes('shipping-protection')) return;

      const reviewBox = this.buildReviewBox(review);
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.classList.add('reviews-carousel__swiper-slide');
      swiperSlide.appendChild(reviewBox);
      this.swiperContainer.appendChild(swiperSlide);
      observeScrollAnimationTriggerElement(swiperSlide);
    });

    setTimeout(() => this.addReadmoreButton(), 500);

    if (this.swiperLoader) {
      this.swiperLoader.initSwiper();
      this.swiperContainer.swiper.slideNext();

      setTimeout(() => {
        const swiperSlides = Array.from(this.swiperContainer.querySelectorAll('swiper-slide'));
        swiperSlides.forEach((swiperSlide, index) => {
          if (index <= 2) {
            swiperSlide.classList.add('scroll-trigger', 'animate--slide-in');
            swiperSlide.setAttribute('data-cascade', 'true');
            swiperSlide.style.setProperty('--animation-order', index + 1);
            observeScrollAnimationTriggerElement(swiperSlide);
          }
        });
      }, 1000);
    }
  }

  async renderAggregatedRating() {
    const aggregatedRating = await this.fetchAggregatedRating();

    const totalRatingValues = aggregatedRating.reviewRatingValuesTotal;
    const totalReviews = aggregatedRating.reviewCount;
    const averageRating = totalRatingValues / totalReviews;
    const starsToDisplay = Math.ceil(averageRating);
    const averageRatingRounded = Math.round(averageRating * 100) / 100;

    const starsContainer = this.aggregatedRatingContainer.querySelector('.aggegated-rating__stars');
    const totalReviewsCountContainer = this.aggregatedRatingContainer.querySelector(
      '.aggegated-rating__total-reviews-count'
    );
    starsContainer.innerHTML = generateStarRating(starsToDisplay);
    totalReviewsCountContainer.textContent = totalReviews.toLocaleString() + ' Reviews';
    this.aggregatedRatingContainer.appendChild(starsContainer);
    this.aggregatedRatingContainer.appendChild(totalReviewsCountContainer);
  }

  async fetchReviews(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (this.productIDs.length > 0) {
        return data?.reviews.filter((review) => {
          return this.productIDs.includes(review.productId.replace('shopify-', ''));
        });
      } else {
        return data?.reviews;
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      return [];
    }
  }

  async fetchAggregatedRating() {
    try {
      const response = await fetch(OKENDO_STORE_URL + '/review_aggregate');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data?.reviewAggregate;
    } catch (error) {
      console.error('Failed to fetch aggregated rating:', error);
      return {};
    }
  }

  buildReviewBox(review) {
    const template = document.getElementById('review-box-template');
    const reviewBox = template.content.cloneNode(true);

    const productImage = reviewBox.querySelector('.review-box__product-image');
    if (productImage) {
      productImage.src = review?.productImageUrl;
      productImage.alt = review?.productName;
    }

    const productTitle = reviewBox.querySelector('.review-box__product-title');
    if (productTitle) productTitle.textContent = review?.productName;

    const messageTitle = reviewBox.querySelector('.review-box__message-title');
    if (messageTitle) messageTitle.textContent = review?.title;

    const messageText = reviewBox.querySelector('.review-box__message-text');
    if (messageText) {
      messageText.setAttribute('data-review-message-text', '');
      messageText.textContent = review?.body;
    }

    const reviewStars = reviewBox.querySelector('.review-box__review-stars');
    if (reviewStars) reviewStars.innerHTML = generateStarRating(review?.rating);

    const reviewAuthor = reviewBox.querySelector('.review-box__review-author');
    if (reviewAuthor) {
      const authorDisplayName = reviewAuthor.querySelector('.author-display-name');
      const authorVerifiedBadge = reviewAuthor.querySelector('.author-verified-badge');
      if (authorDisplayName) authorDisplayName.textContent = review.reviewer?.displayName;
      if (authorVerifiedBadge && !review.reviewer?.isVerified) {
        authorVerifiedBadge.remove();
      }
    }

    const reviewDate = reviewBox.querySelector('.review-box__review-date');
    if (reviewDate) reviewDate.textContent = calculateDateDifference(review.dateCreated).replace('ute', '');

    return reviewBox;
  }

  addReadmoreButton() {
    this.querySelectorAll('[data-review-message-text]').forEach((messageText) => {
      if (messageText.clientHeight < messageText.scrollHeight) {
        const readMoreButton = document.createElement('button');
        readMoreButton.classList.add('review-box__readmore-button');
        readMoreButton.textContent = 'Read more';
        messageText.after(readMoreButton);

        readMoreButton.addEventListener('click', () => {
          expandReviewMessage(messageText);
        });
      }
    });

    const expandReviewMessage = (messageText) => {
      const reviewBox = messageText.closest('.review-box');
      const reviewBoxInner = reviewBox.querySelector('.review-box__inner');

      // Hide the read more button
      const readMoreButton = reviewBox.querySelector('.review-box__readmore-button');
      gsap.to(readMoreButton, { opacity: 0, duration: 0.25 });

      // Hide the review date
      const reviewDate = reviewBox.querySelector('.review-box__review-date');
      gsap.to(reviewDate, { opacity: 0, duration: 0.25 });

      // Add height to the review box message to prevent layout shift
      gsap.set(reviewBox, { height: reviewBox.clientHeight });

      messageText.classList.toggle('expanded');

      // Set the review box info height and position properties
      document.body.appendChild(reviewBoxInner);
      gsap.set(reviewBoxInner, {
        position: 'fixed',
        top: '50%',
        left: '50%',
        overflowY: 'auto',
        transform: 'translate(-50%, -50%)',
        maxWidth: '80rem',
        maxHeight: '90vh',
        height: 'auto',
        zIndex: 1000,
        backgroundColor: 'rgba(248, 243, 233, 1)',
        borderRadius: '14px',
      });

      if (window.innerWidth <= 820) {
        gsap.set(reviewBoxInner, {
          width: 'calc(100% - 2rem)',
        });
      }

      // Create a backdrop and append it to the body
      const backdrop = document.createElement('div');
      backdrop.classList.add('review-box__backdrop');
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', () => {
        closeReviewMessage(messageText, reviewBox, reviewBoxInner);
      });

      // Fade in the backdrop
      gsap.set(backdrop, {
        display: 'block',
        opacity: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        opacity: 0,
        zIndex: 999,
      });

      gsap.to(backdrop, { opacity: 1, duration: 0.25 });

      // Create a close button and append it to the review box message and slowly fade it in
      const closeButton = document.createElement('button');
      closeButton.classList.add('review-box__close-button');
      reviewBoxInner.appendChild(closeButton);
      gsap.to(closeButton, { opacity: 1, scale: 1, duration: 0.25, delay: 0.25, ease: 'power2.out' });

      closeButton.addEventListener('click', () => {
        closeReviewMessage(messageText, reviewBox, reviewBoxInner);
      });
    };

    const closeReviewMessage = (messageText, reviewBox, reviewBoxInner) => {
      // Remove the close button
      const closeButton = reviewBoxInner.querySelector('.review-box__close-button');
      gsap.to(closeButton, { opacity: 0, scale: 0.5, duration: 0.25 }).then(() => closeButton.remove());

      // Display the read more button
      const readMoreButton = reviewBoxInner.querySelector('.review-box__readmore-button');
      gsap.to(readMoreButton, { opacity: 1, duration: 0.25 });

      // Display review date
      const reviewDate = reviewBoxInner.querySelector('.review-box__review-date');
      gsap.to(reviewDate, { opacity: 1, duration: 0.25 });

      // Reset the review box message text height and overflow properties and enable line clamp
      messageText.classList.toggle('expanded');

      // Remove the backdrop
      const backdrop = document.querySelector('.review-box__backdrop');
      gsap.to(backdrop, { opacity: 0, duration: 0.25 }).then(() => backdrop.remove());

      // Reset the review box message inner properties
      gsap.set(reviewBoxInner, {
        clearProps: 'all',
      });

      gsap.set(messageText, {
        clearProps: 'all',
      });

      reviewBox.appendChild(reviewBoxInner);
    };
  }
}

customElements.define('reviews-carousel', ReviewsCarousel);
